import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { temperatureLayout } from '../dashboard/Temperature';

// Карточка асика: фиксированная раскладка в том же языке, что и сводка дашборда.
// Данные приходят готовыми из pages/Device.js; здесь только отображение.

const UNIT_TO_H = { 'H/s': 1, 'KH/s': 1e3, 'kH/s': 1e3, 'MH/s': 1e6, 'GH/s': 1e9, 'TH/s': 1e12, 'PH/s': 1e15, 'EH/s': 1e18 };
const UNIT_STEPS = [['EH/s', 1e18], ['PH/s', 1e15], ['TH/s', 1e12], ['GH/s', 1e9], ['MH/s', 1e6], ['KH/s', 1e3], ['H/s', 1]];

// Хешрейт в единицах устройства → строка с подобранной единицей (214 501,59 GH/s → 214,5 TH/s)
export const formatDeviceHashrate = (value, unit) => {
  const v = Number(value);
  if (!isFinite(v)) return '—';
  const k = UNIT_TO_H[unit];
  if (!k) return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${unit || ''}`.trim();
  const h = v * k;
  if (h === 0) return `0 ${unit}`;
  const [name, size] = UNIT_STEPS.find(([, s]) => h >= s) || UNIT_STEPS[UNIT_STEPS.length - 1];
  const scaled = h / size;
  const digits = scaled >= 100 ? 1 : 2;
  return `${scaled.toLocaleString('ru-RU', { maximumFractionDigits: digits })} ${name}`;
};

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString('ru-RU');
const pad = (n) => String(n).padStart(2, '0');
const fmtDateTime = (ts) => { const d = new Date(ts); return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const fmtTime = (ts) => { const d = new Date(ts); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const toMs = (v) => (v && typeof v.valueOf === 'function' ? new Date(v.valueOf()).getTime() : new Date(v).getTime());
const isOnline = (s) => s === true || String(s).toLowerCase() === 'online';
const isOffline = (s) => s === false || String(s).toLowerCase() === 'offline';

const useColors = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return {
    paper: theme.palette.background.paper,
    tile: dark ? '#1F1F27' : '#EEF0F8',
    line: dark ? '#2B2C33' : '#E3E6EC',
    track: dark ? '#2A2B31' : '#E9EBF0',
    fg: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    faint: dark ? '#6F7480' : '#9AA0AD',
    brand: theme.palette.primary.main,
    ok: dark ? '#30D158' : '#1E9E4A',
    warn: dark ? '#F5B041' : '#B86E00',
    bad: dark ? '#FF5A4F' : '#D92D20',
    okBg: dark ? '#12301C' : '#E3F5E9',
    warnBg: dark ? '#3A2A0C' : '#FBEBD0',
    badBg: dark ? '#3A1512' : '#FDE7E5',
    neutralBg: dark ? '#26272D' : '#ECEEF2',
  };
};

const Card = ({ title, aside, children, c, sx }) => (
  <Box sx={{ background: c.paper, border: `1px solid ${c.line}`, borderRadius: '10px', p: '14px 16px', minWidth: 0, ...sx }}>
    {title && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, mb: 1.25 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: c.muted }}>{title}</span>
        {aside && <span style={{ fontSize: 12, color: c.faint, whiteSpace: 'nowrap' }}>{aside}</span>}
      </Box>
    )}
    {children}
  </Box>
);

const Tile = ({ label, value, extra, sub, c }) => (
  <Box sx={{ background: c.tile, borderRadius: '10px', p: '14px 16px', minWidth: 0 }}>
    <div style={{ fontSize: 13, color: c.muted }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2, marginTop: 2, whiteSpace: 'nowrap', color: c.fg }}>
      {value}{extra}
    </div>
    <div style={{ fontSize: 13, color: c.muted, marginTop: 2 }}>{sub}</div>
  </Box>
);

const Bar = ({ ratio, color, c }) => (
  <div style={{ flex: 1, minWidth: 40, height: 8, borderRadius: 4, background: c.track, overflow: 'hidden' }}>
    <div style={{ width: `${Math.min(100, Math.max(4, ratio * 100))}%`, height: '100%', borderRadius: 4, background: color }} />
  </div>
);

// График хешрейта с линией нормы и лентой работы на общей шкале времени
const HashrateChart = ({ points, unit, norm, uptime, outages, range, c, t }) => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [W, setW] = useState(760);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || 0);
      if (w > 0) setW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = 210, L = 8, R = 8, T = 22, PLOT_B = 162, STRIP_Y = 176;
  const x0 = range.start, x1 = Math.max(range.end, x0 + 1);
  const sx = (ts) => L + ((Math.min(Math.max(ts, x0), x1) - x0) / (x1 - x0)) * (W - L - R);

  const withValue = points.filter((p) => p.v != null);
  const maxV = Math.max(norm || 0, ...withValue.map((p) => p.v), 0) || 1;
  const yMax = maxV * 1.08;
  const sy = (v) => T + (1 - v / yMax) * (PLOT_B - T);

  const gaps = withValue.slice(1).map((p, i) => p.t - withValue[i].t).filter((g) => g > 0).sort((a, b) => a - b);
  const step = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 5 * 60000;
  const segments = [];
  let cur = [];
  for (const p of points) {
    if (p.v == null) { if (cur.length) segments.push(cur); cur = []; continue; }
    if (cur.length && p.t - cur[cur.length - 1].t > step * 3) { segments.push(cur); cur = []; }
    cur.push(p);
  }
  if (cur.length) segments.push(cur);

  // лента: связь по записям статуса, поверх — интервалы без хешрейта и окна сбоев мониторинга
  const strip = [];
  for (const r of uptime || []) {
    const s = Math.max(new Date(r.started_at).getTime(), x0);
    const e = Math.min(r.ended_at ? new Date(r.ended_at).getTime() : x1, x1);
    if (e <= s) continue;
    strip.push({ s, e, color: isOnline(r.status) ? c.ok : isOffline(r.status) ? c.bad : c.faint });
  }
  for (const p of withValue) if (p.v === 0) strip.push({ s: p.t, e: Math.min(p.t + step, x1), color: c.warn, onlyOverOk: true });
  for (const o of outages || []) {
    const s = Math.max(new Date(o.started_at).getTime(), x0), e = Math.min(new Date(o.ended_at).getTime(), x1);
    if (e > s) strip.push({ s, e, color: c.faint });
  }

  const last = withValue[withValue.length - 1];
  const min = withValue.length ? withValue.reduce((m, p) => (p.v < m.v ? p : m), withValue[0]) : null;
  const spanDays = (x1 - x0) / 86400000;
  const tick = (ts) => (spanDays > 2 ? fmtDateTime(ts).slice(0, 5) : fmtTime(ts));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((k) => x0 + k * (x1 - x0));

  const onMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !withValue.length) return;
    const ts = x0 + ((e.clientX - rect.left) / rect.width * W - L) / (W - L - R) * (x1 - x0);
    let best = withValue[0];
    for (const p of withValue) if (Math.abs(p.t - ts) < Math.abs(best.t - ts)) best = p;
    setHover(best);
  };

  return (
    <Box ref={wrapRef} sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 13, mb: 0.5, minHeight: 20, whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <span>
          {hover ? <><b>{formatDeviceHashrate(hover.v, unit)}</b> <span style={{ color: c.muted }}>{fmtDateTime(hover.t)}</span></>
            : <Box component="span" sx={{ color: c.muted, display: { xs: 'none', sm: 'inline' } }}>{withValue.length ? t('cardChartHint') : ''}</Box>}
        </span>
        {min && <span style={{ color: c.muted }}>{t('summaryMin')} {formatDeviceHashrate(min.v, unit)} · {fmtDateTime(min.t)}</span>}
      </Box>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label={t('summaryChartTitle')}>
        {[0, 0.5, 1].map((k) => (
          <g key={k}>
            <line x1={L} x2={W - R} y1={sy(maxV * k)} y2={sy(maxV * k)} stroke={c.line} strokeWidth="1" />
            <text x={L + 2} y={sy(maxV * k) - 3} fontSize="11" fill={c.faint}>{k === 0 ? '0' : formatDeviceHashrate(maxV * k, unit)}</text>
          </g>
        ))}
        {norm > 0 && (
          <g>
            <line x1={L} x2={W - R} y1={sy(norm)} y2={sy(norm)} stroke={c.faint} strokeWidth="1.5" strokeDasharray="5 4" />
            <text x={W - R - 2} y={sy(norm) - 4} fontSize="11" fill={c.muted} textAnchor="end">{t('cardNormLine', { value: formatDeviceHashrate(norm, unit) })}</text>
          </g>
        )}
        {segments.map((seg, i) => (
          <g key={i}>
            <polygon fill={c.ok} fillOpacity="0.12" points={`${sx(seg[0].t)},${sy(0)} ${seg.map((p) => `${sx(p.t)},${sy(p.v)}`).join(' ')} ${sx(seg[seg.length - 1].t)},${sy(0)}`} />
            <polyline fill="none" stroke={c.ok} strokeWidth="2" strokeLinejoin="round" points={seg.map((p) => `${sx(p.t)},${sy(p.v)}`).join(' ')} />
          </g>
        ))}
        {withValue.length === 0 && <text x={W / 2} y={(T + PLOT_B) / 2} fontSize="13" fill={c.muted} textAnchor="middle">{t('summaryChartEmpty')}</text>}
        {(hover || last) && <circle cx={sx((hover || last).t)} cy={sy((hover || last).v)} r="4" fill={c.ok} />}
        {hover && <line x1={sx(hover.t)} x2={sx(hover.t)} y1={T} y2={STRIP_Y + 10} stroke={c.faint} strokeWidth="1" strokeDasharray="3 3" />}
        <rect x={L} y={STRIP_Y} width={W - L - R} height="10" rx="5" fill={c.track} />
        {strip.map((s, i) => (
          <rect key={i} x={sx(s.s)} y={STRIP_Y} width={Math.max(1.5, sx(s.e) - sx(s.s))} height="10" fill={s.color} />
        ))}
        {ticks.map((ts, i) => (
          <text key={i} x={Math.min(Math.max(sx(ts), L + 18), W - R - 18)} y={H - 6} fontSize="11" fill={c.faint} textAnchor="middle">{tick(ts)}</text>
        ))}
      </svg>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 12, color: c.muted, mt: 0.75 }}>
        {[[c.ok, 'cardLegendHashing'], [c.warn, 'cardLegendIdle'], [c.bad, 'cardLegendOffline'], [c.faint, 'cardLegendOther']].map(([color, key]) => (
          <span key={key}><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color, marginRight: 6, verticalAlign: -1 }} />{t(key)}</span>
        ))}
      </Box>
    </Box>
  );
};

const DeviceCard = ({
  deviceData, chartData, alertRules, dateRange, periodPreset, onPresetChange, onCustomPeriod,
  summary, kwhPrice, outages, events, statusInfo, latestStale, latestAt,
  canEdit, onEdit, onThresholds, onBack, logsNode,
}) => {
  const { t } = useTranslation('dashboard');
  const c = useColors();
  const device = deviceData?.device || {};
  const unit = device.hashrate_unit || 'TH/s';
  const latest = deviceData?.latest_metrics?.temperature || null;
  // нет значения хешрейта в последнем измерении — это «нет данных», а не ноль
  const hashrateNow = latest && !latestStale && latest.hashrate != null ? Number(latest.hashrate) || 0 : null;
  const norm = Number(summary?.norm_hashrate) > 0 ? Number(summary.norm_hashrate) : null;
  const pct = norm && hashrateNow != null ? Math.round((hashrateNow / norm) * 100) : null;

  const range = useMemo(() => ({ start: toMs(dateRange.start), end: toMs(dateRange.end) }), [dateRange]);
  const uptime = chartData?.uptime_data || deviceData?.uptime_data || [];
  const points = useMemo(() => (chartData?.hashrate_data || deviceData?.hashrate_data || [])
    .map((p) => ({ t: new Date(p.time || p.created_at || p.bucket).getTime(), v: p.isNull || p.hashrate == null ? null : Number(p.hashrate) || 0 }))
    .filter((p) => !isNaN(p.t)).sort((a, b) => a.t - b.t), [chartData, deviceData]);

  // аптайм за период: на связи ÷ (период − ремонт и прочие статусы − окна сбоев мониторинга)
  const uptimePct = useMemo(() => {
    let on = 0, excluded = 0;
    for (const r of uptime) {
      const s = Math.max(new Date(r.started_at).getTime(), range.start);
      const e = Math.min(r.ended_at ? new Date(r.ended_at).getTime() : range.end, range.end);
      if (e <= s) continue;
      if (isOnline(r.status)) on += e - s; else if (!isOffline(r.status)) excluded += e - s;
    }
    for (const o of outages || []) {
      const s = Math.max(new Date(o.started_at).getTime(), range.start), e = Math.min(new Date(o.ended_at).getTime(), range.end);
      if (e > s) excluded += e - s;
    }
    const available = range.end - range.start - excluded;
    return uptime.length && available > 0 ? Math.min(100, Math.round((on / available) * 1000) / 10) : null;
  }, [uptime, outages, range]);

  // статус словами
  let chip = { text: t('cardStatusUnknown'), color: c.muted, bg: c.neutralBg };
  if (statusInfo.status === true) {
    if (latestStale) chip = { text: t('cardStatusNoData'), color: c.warn, bg: c.warnBg };
    else if (hashrateNow == null) chip = { text: t('cardStatusNoData'), color: c.warn, bg: c.warnBg };
    else if (!(hashrateNow > 0)) chip = { text: t('cardStatusIdle'), color: c.warn, bg: c.warnBg };
    else if (pct != null && pct < 85) chip = { text: t('cardStatusLow', { pct }), color: c.warn, bg: c.warnBg };
    else chip = { text: pct != null ? t('cardStatusWorkingPct', { pct }) : t('cardStatusWorking'), color: c.ok, bg: c.okBg };
  } else if (statusInfo.status === false) {
    chip = { text: t('cardStatusOffline'), color: c.bad, bg: c.badBg };
  } else if (typeof statusInfo.status === 'string') {
    chip = { text: statusInfo.status.charAt(0).toUpperCase() + statusInfo.status.slice(1), color: c.muted, bg: c.neutralBg };
  }
  if (statusInfo.duration) chip.text = `${chip.text} · ${statusInfo.duration}`;

  const sn = device.serial_number && !/^unknown$/i.test(device.serial_number) ? device.serial_number : null;
  const none = (key) => (
    <span style={{ color: c.faint }}>
      {t(key)}{canEdit && onEdit && <> · <span role="button" tabIndex={0} onClick={onEdit} onKeyDown={(e) => e.key === 'Enter' && onEdit()} style={{ color: c.brand, fontWeight: 600, cursor: 'pointer' }}>{t('cardSet')}</span></>}
    </span>
  );
  const passport = [
    [t('cardOwner'), device.owner_name || none('cardNoOwner')],
    [t('infoHosting'), deviceData?.hosting?.name || none('cardNoHosting')],
    [t('infoLocation'), deviceData?.location?.name || none('cardNoLocation')],
    [t('infoAlgorithm'), device.algorithm || '—'],
    [t('infoMac'), device.mac && !/^unknown$/i.test(device.mac) ? device.mac : '—'],
    [t('infoSerial'), sn || '—'],
    [t('infoProfile'), device.profile_hashrate
      ? `${formatDeviceHashrate(device.profile_hashrate, unit)}${device.profile_power ? ` · ${fmtInt(device.profile_power)} ${t('cardW')}` : ''}`
      : <span style={{ color: c.faint }}>{t('cardNoProfile')}</span>],
    [t('cardBillingPower'), summary?.power ? `${fmtInt(summary.power)} ${t('cardW')}` : none('cardNoPower')],
  ];

  // температуры и вентиляторы — последнее измерение
  const layout = temperatureLayout(device.model_name || device.model, [3, 4, 5].some((i) => latest?.[`temperature${i}`] != null));
  const tempCols = [['chips', t('tempChips'), layout.chips], ['boards', t('tempBoards'), layout.boards]]
    .filter(([, , sensors]) => sensors.some((i) => latest?.[`temperature${i}`] != null));
  const tempLimit = (sensors) => {
    const limits = [...new Set(sensors.map((i) => alertRules?.[`temperature${i}_max`]).filter((v) => v != null).map(Number))];
    if (!limits.length) return t('tempNoLimit');
    return limits.length === 1 ? t('tempLimit', { value: limits[0] }) : t('tempLimitRange', { min: Math.min(...limits), max: Math.max(...limits) });
  };
  const tempColor = (v, max) => (v > max ? c.bad : v >= max * 0.85 ? c.warn : c.ok);
  const fans = [0, 1, 2, 3].map((i) => ({ i, v: latest?.[`fan_speed${i}`], min: alertRules?.[`fan_speed${i}_min`], max: alertRules?.[`fan_speed${i}_max`] })).filter((f) => f.v != null);
  const fanMins = [...new Set(fans.map((f) => f.min).filter((v) => v != null).map(Number))];
  const fanScale = Math.max(...fans.map((f) => Math.max(Number(f.v) || 0, Number(f.max) || 0)), 1);
  const staleNote = latestStale && latestAt ? t('deviceAsOf', { time: fmtDateTime(latestAt) }) : t('summaryNow');

  const pools = (deviceData?.pools || []).slice().sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  const pool = pools.find((p) => p.active) || pools[0];
  const accepted = Number(device.accepted_shares) || 0, rejected = Number(device.rejected_shares) || 0;
  const rejectPct = accepted + rejected > 0 ? (rejected / (accepted + rejected)) * 100 : null;

  const metricLabel = (m) => {
    if (!m) return '';
    if (m === 'hashrate') return t('cardMetricHashrate');
    const temp = /^temperature(\d)$/.exec(m);
    if (temp) {
      const i = Number(temp[1]);
      const ci = layout.chips.indexOf(i), bi = layout.boards.indexOf(i);
      return ci >= 0 ? t('cardMetricChip', { n: ci + 1 }) : t('cardMetricBoard', { n: (bi >= 0 ? bi : i) + 1 });
    }
    const fan = /^fan_speed(\d)$/.exec(m);
    return fan ? t('cardMetricFan', { n: Number(fan[1]) + 1 }) : m;
  };
  const eventText = (e) => {
    const val = e.value == null ? '' : e.metric === 'hashrate' ? formatDeviceHashrate(e.value, unit)
      : /^temperature/.test(e.metric || '') ? `${Math.round(e.value)}°` : fmtInt(e.value);
    const deg = /^temperature/.test(e.metric || '') ? '°' : '';
    const lim = e.limit_max != null && e.value > e.limit_max ? t('cardEventAbove', { limit: `${fmtInt(e.limit_max)}${deg}` })
      : e.limit_min != null && e.value < e.limit_min ? t('cardEventBelow', { limit: e.metric === 'hashrate' ? formatDeviceHashrate(e.limit_min, unit) : fmtInt(e.limit_min) }) : '';
    if (e.kind === 'offline') return t('cardEventOffline');
    if (e.kind === 'online') return t('cardEventOnline');
    if (e.kind === 'unstable') return t('cardEventUnstable');
    if (e.kind === 'recovered') return t('cardEventRecovered', { metric: metricLabel(e.metric), value: val });
    return `${metricLabel(e.metric)} — ${val}${lim ? ` ${lim}` : ''}`;
  };
  const eventTag = (e) => {
    if (e.kind === 'recovered' || e.kind === 'online') return [t('cardTagRecovered'), c.ok];
    if (e.kind === 'reminder') return [t('cardTagReminder'), c.warn];
    if (e.kind === 'unstable') return [t('cardTagUnstable'), c.warn];
    return [e.severity === 'critical' || e.kind === 'offline' ? t('cardTagCritical') : t('cardTagAlert'), e.severity === 'critical' || e.kind === 'offline' ? c.bad : c.warn];
  };

  const kv = { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '7px 14px', margin: 0, fontSize: 14 };
  const row = { display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, px: 2, py: 2.5, fontVariantNumeric: 'tabular-nums', color: c.fg }}>
      <Box sx={{ fontSize: 13, color: c.muted }}>
        <span role="button" tabIndex={0} onClick={onBack} onKeyDown={(e) => e.key === 'Enter' && onBack()} style={{ color: c.brand, fontWeight: 600, cursor: 'pointer' }}>← {t('cardBack')}</span>
        {deviceData?.hosting?.name ? ` / ${deviceData.hosting.name}` : ''}{device.ip_address ? ` / ${device.ip_address}` : ''}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h1" sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, m: 0 }}>{device.model_name || device.model || '—'}</Box>
          <Box sx={{ fontSize: 14, color: c.muted }}>
            {[device.ip_address, sn ? `SN …${sn.slice(-5)}` : null, device.owner_name].filter(Boolean).join(' · ')}
          </Box>
        </Box>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, padding: '5px 12px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', color: chip.color, background: chip.bg }}>
          <i style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }} />{chip.text}
        </span>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={periodPreset} onChange={(e, v) => v && v !== 'custom' && onPresetChange(v)} sx={{ background: c.paper }}>
          <ToggleButton value="1h" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange1h')}</ToggleButton>
          <ToggleButton value="24h" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange24h')}</ToggleButton>
          <ToggleButton value="3d" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange3d')}</ToggleButton>
          <ToggleButton value="30d" sx={{ textTransform: 'none', px: 1.5 }}>{t('summaryRange30d')}</ToggleButton>
          <ToggleButton value="custom" sx={{ textTransform: 'none', px: 1.5 }} onClick={onCustomPeriod}>{t('summaryCustomPeriod')}</ToggleButton>
        </ToggleButtonGroup>
        {canEdit && onEdit && <Button variant="outlined" size="small" onClick={onEdit} sx={{ height: 36 }}>{t('cardEdit')}</Button>}
        {canEdit && onThresholds && <Button variant="outlined" size="small" onClick={onThresholds} sx={{ height: 36 }}>{t('cardThresholds')}</Button>}
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' } }}>
        <Tile c={c} label={latestStale && latestAt ? t('cardHashrateAsOf', { time: fmtDateTime(latestAt) }) : t('cardHashrateNow')}
          value={latest && latest.hashrate != null ? formatDeviceHashrate(latest.hashrate, unit) : '—'}
          extra={pct != null && <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 8, color: pct < 85 ? c.warn : c.ok }}>{pct}%</span>}
          sub={norm ? (
            <Tooltip title={t('summaryNormHint')}><span>{t('cardNorm', { value: formatDeviceHashrate(norm, unit) })} · {t(summary.norm_source === 'profile' ? 'cardNormProfile' : 'cardNormPosition')}</span></Tooltip>
          ) : t('cardNormUnknown')} />
        <Tile c={c} label={t('summaryUptime')} value={uptimePct != null ? `${uptimePct.toLocaleString('ru-RU')}%` : '—'} sub={t('summaryForPeriod')} />
        <Tile c={c} label={t('cardConsumption')} value={summary?.power ? `${fmtInt(summary.power)} ${t('cardW')}` : '—'}
          sub={summary ? t('summaryKwhPeriod', { value: (Number(summary.kwh) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) }) : t('summaryNoData')} />
        <Tile c={c} label={t('summaryAmount')} value={summary ? `${fmtInt(summary.amount)} ₽` : '—'}
          sub={summary ? `${kwhPrice != null ? `${t('summaryPrice', { price: Number(kwhPrice).toLocaleString('ru-RU') })} · ` : ''}${t('cardHoursWorked', { hours: ((summary.minutes_worked || 0) / 60).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) })}` : t('summaryNoData')} />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1.9fr) minmax(0,1fr)' } }}>
        <Card c={c} title={t('summaryChartTitle')}>
          <HashrateChart points={points} unit={unit} norm={norm} uptime={uptime} outages={outages} range={range} c={c} t={t} />
        </Card>
        <Card c={c} title={t('cardPassport')}>
          <dl style={kv}>
            {passport.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt style={{ color: c.muted }}>{k}</dt>
                <dd style={{ margin: 0, textAlign: 'right', overflowWrap: 'anywhere' }}>{v}</dd>
              </React.Fragment>
            ))}
          </dl>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1.25fr) minmax(0,1fr) minmax(0,1fr)' } }}>
        <Card c={c} title={t('tempTitle')} aside={latest ? staleNote : null}>
          {tempCols.length ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: `64px repeat(${tempCols.length}, minmax(0, 1fr))`, columnGap: 2, rowGap: 0.75, alignItems: 'center', fontSize: 14 }}>
              <div />
              {tempCols.map(([key, title, sensors]) => (
                <div key={key} style={{ borderBottom: `1px solid ${c.line}`, paddingBottom: 5, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
                  <div style={{ fontSize: 12, color: c.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tempLimit(sensors)}</div>
                </div>
              ))}
              {[0, 1, 2].map((r) => (
                <React.Fragment key={r}>
                  <div style={{ color: c.muted, fontSize: 13, whiteSpace: 'nowrap' }}>{t('tempHashboard', { n: r + 1 })}</div>
                  {tempCols.map(([key, , sensors]) => {
                    const i = sensors[r];
                    const v = i === undefined || latest?.[`temperature${i}`] == null ? null : Math.round(Number(latest[`temperature${i}`]));
                    if (v == null) return <div key={key} style={{ color: c.faint, textAlign: 'right' }}>—</div>;
                    const max = Number(alertRules?.[`temperature${i}_max`]) || 90;
                    const color = tempColor(v, max);
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <Bar ratio={v / max} color={color} c={c} />
                        <b style={{ width: 38, textAlign: 'right', color, flexShrink: 0 }}>{v}°</b>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          ) : <div style={{ color: c.muted, fontSize: 14 }}>{t('tempNoData')}</div>}
        </Card>

        <Card c={c} title={t('cardFans')} aside={fanMins.length === 1 ? t('cardFanMin', { value: fmtInt(fanMins[0]) }) : null}>
          {fans.length ? fans.map((f) => {
            const low = f.min != null && Number(f.v) < Number(f.min);
            const color = low ? c.bad : c.ok;
            return (
              <div key={f.i} style={row}>
                <span style={{ width: 18, color: c.muted, fontSize: 13 }}>{f.i + 1}</span>
                <Bar ratio={Number(f.v) / fanScale} color={color} c={c} />
                <b style={{ width: 62, textAlign: 'right', color }}>{fmtInt(f.v)}</b>
              </div>
            );
          }) : <div style={{ color: c.muted, fontSize: 14 }}>{t('cardNoFans')}</div>}
          {fans.length > 0 && <div style={{ fontSize: 12, color: c.faint, marginTop: 4 }}>{t('cardRpm')}</div>}
        </Card>

        <Card c={c} title={t('cardPoolShares')}>
          <dl style={kv}>
            <dt style={{ color: c.muted }}>{t('cardPool')}</dt>
            <dd title={pool?.link || undefined} style={{ margin: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pool?.link || <span style={{ color: c.faint }}>{t('cardNoPool')}</span>}</dd>
            {pool?.worker && <><dt style={{ color: c.muted }}>{t('cardWorker')}</dt><dd style={{ margin: 0, textAlign: 'right', overflowWrap: 'anywhere' }}>{pool.worker}</dd></>}
            <dt style={{ color: c.muted }}>{t('cardAccepted')}</dt><dd style={{ margin: 0, textAlign: 'right' }}>{fmtInt(accepted)}</dd>
            <dt style={{ color: c.muted }}>{t('cardRejected')}</dt>
            <dd style={{ margin: 0, textAlign: 'right' }}>
              {fmtInt(rejected)}{rejectPct != null && <> · <span style={{ color: rejectPct > 2 ? c.warn : c.ok }}>{rejectPct.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%</span></>}
            </dd>
          </dl>
        </Card>
      </Box>

      <Card c={c} title={t('cardEvents')} aside={t('cardEventsHint')}>
        {events === null && <div style={{ color: c.muted, fontSize: 14 }}>{t('summaryLoading')}</div>}
        {events !== null && events.length === 0 && <div style={{ color: c.muted, fontSize: 14 }}>{t('cardEventsEmpty')}</div>}
        {events !== null && events.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gap: '6px 12px', fontSize: 14 }}>
            {events.map((e) => {
              const [tag, color] = eventTag(e);
              return (
                <React.Fragment key={e.id}>
                  <time style={{ color: c.muted, whiteSpace: 'nowrap' }}>{fmtDateTime(e.occurred_at)}</time>
                  <span>{eventText(e)}</span>
                  <span style={{ color, whiteSpace: 'nowrap' }}>{tag}</span>
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Card>

      {logsNode}
    </Box>
  );
};

export default DeviceCard;
